import {Trans} from 'react-i18next'
import messages from '../../../../assets/messages'
import {getFormattedAmount} from '../../../../common/utils/general'
import {PammOfferType} from '../../../../graphql/types'
import {PerfFeeTooltip} from '../PerfFeeTooltip'
import {getIntervalUnitMessage} from './intervalMessages'
import {StatCell, StatLabel, StatsGrid, StatValue} from './OfferStatsGrid.styled'

type OfferStatsGridProps = {
  offer: PammOfferType
}

/**
 * Bottom grid of an offer card: performance fee (with a per-tier tooltip), trading
 * interval, and the minimum deposit / withdrawal / initial investment amounts.
 *
 * @param offer - The PAMM offer whose settings populate the grid.
 */
export const OfferStatsGrid = ({offer}: OfferStatsGridProps) => {
  const settings = offer.settings
  const currency = offer.currency ?? 'USD'
  const performanceFeeValue = settings?.performanceFees?.levels?.[0]?.value
  const interval = settings?.tradingInterval
  const intervalUnit = getIntervalUnitMessage(interval)

  return (
    <StatsGrid>
      <StatCell>
        <StatLabel>
          <Trans {...messages.pammPerfFeeShort} />
          <PerfFeeTooltip fees={settings?.performanceFees} currency={currency} />
        </StatLabel>
        <StatValue>
          {typeof performanceFeeValue === 'number' ? (
            `${performanceFeeValue}%`
          ) : (
            <Trans {...messages.pammPerfFeeNotSet} />
          )}
        </StatValue>
      </StatCell>

      <StatCell>
        <StatLabel>
          <Trans {...messages.pammTradingIntervalShort} />
        </StatLabel>
        <StatValue>
          {interval?.count ?? 0} {intervalUnit ? <Trans {...intervalUnit} /> : null}
        </StatValue>
      </StatCell>

      <StatCell>
        <StatLabel>
          <Trans {...messages.minDeposit} />
        </StatLabel>
        <StatValue green>{getFormattedAmount({amount: settings?.minDeposit ?? 0, currency})}</StatValue>
      </StatCell>

      <StatCell>
        <StatLabel>
          <Trans {...messages.minWithdrawal} />
        </StatLabel>
        <StatValue>{getFormattedAmount({amount: settings?.minWithdrawal ?? 0, currency})}</StatValue>
      </StatCell>

      <StatCell>
        <StatLabel>
          <Trans {...messages.minInitialInvestment} />
        </StatLabel>
        <StatValue green>{getFormattedAmount({amount: settings?.minInitialInvestment ?? 0, currency})}</StatValue>
      </StatCell>
    </StatsGrid>
  )
}
